import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import styled from "@emotion/styled";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ContentBox from "../../components/ui/ContentBox";
import ButtonSquare from "../../components/ui/ButtonSquare";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import { ToastContainer, toast } from "react-toastify";
import { ErrorMessage, Field, Form, Formik } from "formik";
import FormikErrorText from "../../components/ui/FormikErrorText";
import * as yup from "yup";
import { modalStyleOverflow } from "../../theme/ModalStyle";

const TYPE_OPTIONS = [
  { value: "fixed_monthly", label: "Fixed Monthly" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_session", label: "Per Session" },
  { value: "per_player", label: "Per Player" },
  { value: "revenue_percentage", label: "Revenue %" },
];

const SCOPE_OPTIONS = [
  { value: "all", label: "All Lessons" },
  { value: "group_lesson", label: "Group Lessons" },
  { value: "private_lesson", label: "Private Lessons" },
  { value: "program", label: "Specific Program" },
];

function SalaryComponents() {
  const { user } = useAuthContext();
  const access_token = user?.access_token;

  const [components, setComponents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(null);
  const [generateYear, setGenerateYear] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [change, setChange] = useState(false);

  useEffect(() => {
    document.title = "Moody admin - Salary Components";
  }, []);

  // Fetch components
  useEffect(() => {
    setLoading(true);
    Api.get(Urls.GET_SALARY_COMPONENTS, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
      params: { all: 1 },
    })
      .then((res) => {
        setComponents(res?.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.response?.data?.message || "Error loading components");
      });
  }, [change]);

  // Fetch programs for dropdown
  useEffect(() => {
    Api.get(Urls.GET_PROGRAMS, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
    })
      .then((res) => {
        setPrograms(res?.data?.data || []);
      })
      .catch(() => {});
  }, []);

  const handleCreate = (values, { setSubmitting }) => {
    Api.post(
      Urls.CREATE_SALARY_COMPONENT,
      {
        name: values.name,
        type: values.type,
        scope_type: values.scope_type,
        scope_id: values.scope_type === "program" ? values.scope_id : null,
        default_value: values.default_value,
        is_active: values.is_active,
      },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then(() => {
        toast.success("Component created successfully");
        setOpenCreate(false);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error creating component");
      })
      .finally(() => setSubmitting(false));
  };

  const handleEdit = (values, { setSubmitting }) => {
    Api.patch(
      Urls.UPDATE_SALARY_COMPONENT + selectedComponent.id,
      {
        name: values.name,
        type: values.type,
        scope_type: values.scope_type,
        scope_id: values.scope_type === "program" ? values.scope_id : null,
        default_value: values.default_value,
        is_active: values.is_active,
      },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then(() => {
        toast.success("Component updated successfully");
        setOpenEdit(false);
        setSelectedComponent(null);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error updating component");
      })
      .finally(() => setSubmitting(false));
  };

  const handleDelete = () => {
    Api.delete(Urls.DELETE_SALARY_COMPONENT + selectedComponent.id, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Component deleted successfully");
        setOpenDelete(false);
        setSelectedComponent(null);
        setChange(!change);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error deleting component");
      });
  };

  const getTypeLabel = (type) => TYPE_OPTIONS.find((t) => t.value === type)?.label || type;
  const getScopeLabel = (scopeType, component) => {
    if (scopeType === "program" && component?.program) return `Program: ${component.program.name}`;
    return SCOPE_OPTIONS.find((s) => s.value === scopeType)?.label || scopeType;
  };

  const handleGenerate = () => {
    if (!generateMonth || !generateYear) {
      toast.error("Please select month and year");
      return;
    }
    setGenerating(true);
    Api.post(
      Urls.GENERATE_COACH_PAYSLIPS,
      { month: generateMonth, year: generateYear },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then((res) => {
        toast.success(res?.data?.message || "Payslips generated successfully");
        setOpenGenerate(false);
        setGenerating(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error generating payslips");
        setGenerating(false);
      });
  };

  const MONTHS = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const createValidation = yup.object({
    name: yup.string().required("Name is required"),
    type: yup.string().required("Type is required"),
    scope_type: yup.string().required("Scope is required"),
    scope_id: yup.number().nullable().when("scope_type", {
      is: "program",
      then: (schema) => schema.required("Program is required"),
    }),
    default_value: yup.number().min(0, "Must be positive").required("Default value is required"),
  });

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "white",
      paddingTop: "8px",
      paddingBottom: "8px",
      letterSpacing: "1px",
      color: "#4B465C",
      fontSize: 13,
      fontWeight: 500,
    },
    [`&.${tableCellClasses.body}`]: {
      background: "white",
      fontSize: 14,
      color: "#666D83",
      fontWeight: "500",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:last-child td, &:last-child th": { border: 0 },
    ":hover": { filter: "brightness(0.95)" },
  }));

  const StyledTable = styled(Table)({ borderRadius: "0px" });

  const ComponentForm = ({ initialValues, onSubmit, submitLabel }) => (
    <Formik initialValues={initialValues} validationSchema={createValidation} onSubmit={onSubmit} enableReinitialize>
      {({ values, setFieldValue, isSubmitting }) => (
        <Form>
          <Box display="flex" flexDirection="column" gap="16px">
            <Box>
              <Typography fontSize="13px" fontWeight="500" color="#4B465C">Name</Typography>
              <Field name="name" as={TextField} fullWidth size="small" placeholder="e.g. Group Lessons - Hourly Rate" />
              <ErrorMessage name="name" component={FormikErrorText} />
            </Box>
            <Box>
              <Typography fontSize="13px" fontWeight="500" color="#4B465C">Type</Typography>
              <Autocomplete
                options={TYPE_OPTIONS}
                getOptionLabel={(o) => o.label}
                value={TYPE_OPTIONS.find((o) => o.value === values.type) || null}
                onChange={(_, v) => setFieldValue("type", v?.value || "")}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select type" />}
              />
              <ErrorMessage name="type" component={FormikErrorText} />
            </Box>
            <Box>
              <Typography fontSize="13px" fontWeight="500" color="#4B465C">Scope</Typography>
              <Autocomplete
                options={SCOPE_OPTIONS}
                getOptionLabel={(o) => o.label}
                value={SCOPE_OPTIONS.find((o) => o.value === values.scope_type) || null}
                onChange={(_, v) => {
                  setFieldValue("scope_type", v?.value || "");
                  if (v?.value !== "program") setFieldValue("scope_id", null);
                }}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select scope" />}
              />
              <ErrorMessage name="scope_type" component={FormikErrorText} />
            </Box>
            {values.scope_type === "program" && (
              <Box>
                <Typography fontSize="13px" fontWeight="500" color="#4B465C">Program</Typography>
                <Autocomplete
                  options={programs}
                  getOptionLabel={(o) => o.name || ""}
                  value={programs.find((p) => p.id === values.scope_id) || null}
                  onChange={(_, v) => setFieldValue("scope_id", v?.id || null)}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select program" />}
                />
                <ErrorMessage name="scope_id" component={FormikErrorText} />
              </Box>
            )}
            <Box>
              <Typography fontSize="13px" fontWeight="500" color="#4B465C">
                Default Value {values.type === "revenue_percentage" ? "(%)" : "(BHD)"}
              </Typography>
              <Field name="default_value" as={TextField} fullWidth size="small" type="number" inputProps={{ min: 0, step: 0.01 }} />
              <ErrorMessage name="default_value" component={FormikErrorText} />
            </Box>
            <FormControlLabel
              control={<Switch checked={values.is_active} onChange={(e) => setFieldValue("is_active", e.target.checked)} />}
              label="Active"
            />
            <Box display="flex" justifyContent="flex-end" gap="8px">
              <Button variant="primary_inactive" onClick={() => { setOpenCreate(false); setOpenEdit(false); }}>Cancel</Button>
              <Button type="submit" variant="primary_active" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} /> : submitLabel}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );

  return (
    <Box display="flex" flexDirection="column" gap="26px" marginTop="26px">
      <ToastContainer />
      <Box>
        <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
          Salary Components
        </Typography>
      </Box>

      <ContentBox>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
              Components
            </Typography>
            <Box display="flex" gap="8px">
              <Button variant="primary_inactive" onClick={() => setOpenGenerate(true)}>
                Generate Payslips
              </Button>
              <Button variant="primary_active" onClick={() => setOpenCreate(true)}>
                + Add Component
              </Button>
            </Box>
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center"><CircularProgress /></Box>
          )}

          {!loading && (
            <TableContainer sx={{ borderRadius: "0px", border: "1px solid #DBDADE" }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>NAME</StyledTableCell>
                    <StyledTableCell>TYPE</StyledTableCell>
                    <StyledTableCell>SCOPE</StyledTableCell>
                    <StyledTableCell align="center">DEFAULT VALUE</StyledTableCell>
                    <StyledTableCell align="center">STATUS</StyledTableCell>
                    <StyledTableCell align="center">ACTIONS</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {components.map((c) => (
                    <StyledTableRow key={c.id}>
                      <StyledTableCell>{c.name}</StyledTableCell>
                      <StyledTableCell>{getTypeLabel(c.type)}</StyledTableCell>
                      <StyledTableCell>{getScopeLabel(c.scope_type, c)}</StyledTableCell>
                      <StyledTableCell align="center">
                        {c.type === "revenue_percentage" ? `${c.default_value}%` : `${c.default_value} BHD`}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip
                          label={c.is_active ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            backgroundColor: c.is_active ? "#E8F5E9" : "#FFEBEE",
                            color: c.is_active ? "#2E7D32" : "#C62828",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box display="flex" justifyContent="center" gap="4px">
                          <ButtonSquare
                            action="edit_outlined"
                            onClick={() => {
                              setSelectedComponent(c);
                              setOpenEdit(true);
                            }}
                          />
                          <ButtonSquare
                            action="delete_red"
                            onClick={() => {
                              setSelectedComponent(c);
                              setOpenDelete(true);
                            }}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}

          {!loading && components.length === 0 && (
            <Typography textAlign="center" color="#666D83">No salary components configured yet</Typography>
          )}
        </Box>
      </ContentBox>

      {/* Create Modal */}
      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="16px">
            Add Salary Component
          </Typography>
          <ComponentForm
            initialValues={{ name: "", type: "", scope_type: "all", scope_id: null, default_value: 0, is_active: true }}
            onSubmit={handleCreate}
            submitLabel="Create"
          />
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal open={openEdit} onClose={() => { setOpenEdit(false); setSelectedComponent(null); }}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="16px">
            Edit Salary Component
          </Typography>
          {selectedComponent && (
            <ComponentForm
              initialValues={{
                name: selectedComponent.name,
                type: selectedComponent.type,
                scope_type: selectedComponent.scope_type,
                scope_id: selectedComponent.scope_id,
                default_value: selectedComponent.default_value,
                is_active: selectedComponent.is_active,
              }}
              onSubmit={handleEdit}
              submitLabel="Save"
            />
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={openDelete} onClose={() => { setOpenDelete(false); setSelectedComponent(null); }}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="8px">
            Delete Component
          </Typography>
          <Typography fontSize="14px" color="#666D83" mb="16px">
            Are you sure you want to delete "{selectedComponent?.name}"? This will also remove all coach overrides for this component.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap="8px">
            <Button variant="primary_inactive" onClick={() => { setOpenDelete(false); setSelectedComponent(null); }}>Cancel</Button>
            <Button variant="primary_active" onClick={handleDelete}>Delete</Button>
          </Box>
        </Box>
      </Modal>

      {/* Generate Payslips Modal */}
      <Modal open={openGenerate} onClose={() => setOpenGenerate(false)}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb="8px">
            Generate Payslips
          </Typography>
          <Typography fontSize="14px" color="#666D83" mb="16px">
            Calculate payslips for all coaches for the selected month. This will use the current salary components and coach overrides.
          </Typography>
          <Box display="flex" gap="16px" mb="16px">
            <Autocomplete
              options={MONTHS}
              getOptionLabel={(o) => o.label}
              value={MONTHS.find((m) => m.value === generateMonth) || null}
              onChange={(_, v) => setGenerateMonth(v?.value || null)}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Month" />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={yearOptions}
              getOptionLabel={(o) => String(o)}
              value={generateYear || null}
              onChange={(_, v) => setGenerateYear(v || null)}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Year" />}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap="8px">
            <Button variant="primary_inactive" onClick={() => setOpenGenerate(false)}>Cancel</Button>
            <Button variant="primary_active" onClick={handleGenerate} disabled={generating}>
              {generating ? <CircularProgress size={20} /> : "Generate"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default SalaryComponents;
